// Per-book EV and preferred-book pickers used by the results filter/sort pipeline
// (hooks/useFilteredAndSortedOptions). Shared by the results page and the
// Background Queries card counts so both rank and count with identical math.
import { cleanPerBookEv, isUnbackableBookData } from './canonicalEv';

// Card-consistent per-book EV: the same leave-one-out cleanPerBookEv path as the
// UnifiedBetCard rail and BetCardV2 pill, for SGPs and straights.
export function makeSortEvForBook(consensusOptions) {
    return (raw, bookData) => {
        if (!bookData) return null;
        const isSGPItem = raw?.ev_method === 'individual_leg_devig';
        if (isSGPItem) {
            const ev = cleanPerBookEv(raw, bookData, true, consensusOptions);
            if (ev !== null) return ev;
            if (typeof bookData === 'object' && typeof bookData.ev_percent === 'number') {
                return bookData.ev_percent;
            }
            return null;
        }
        return cleanPerBookEv(raw, bookData, false, consensusOptions);
    };
}

// Key of the best book in a bookOdds map.
// - My Books mode: highest-EV book among the user's books (price only when no EV).
// - Otherwise: the highest-priced book.
export function makePreferredBook({ wantMyBooks, myBooksList, sortEvForBook }) {
    return (bookOdds, rawForEv = null) => {
        const bo = bookOdds || {};
        if (wantMyBooks && myBooksList.length > 0) {
            let bestBook = null, bestEv = -Infinity, bestPrice = -Infinity, hadEv = false;
            for (const [b, d] of Object.entries(bo)) {
                if (isUnbackableBookData(d, b)) continue;
                const bl = b.toLowerCase();
                const isMine = myBooksList.some(mb => bl === mb.toLowerCase() || bl.includes(mb.toLowerCase()));
                if (!isMine) continue;
                let ev = null;
                if (rawForEv) {
                    try {
                        const cpe = sortEvForBook(rawForEv, d);
                        if (Number.isFinite(cpe)) ev = cpe;
                    } catch { /* ignore */ }
                }
                if (ev == null && Number.isFinite(d?.ev_percent)) ev = d.ev_percent;
                if (ev != null && Number.isFinite(ev)) {
                    hadEv = true;
                    if (ev > bestEv) { bestEv = ev; bestBook = b; }
                } else if (!hadEv) {
                    const price = d?.price;
                    if (typeof price === 'number' && price > bestPrice) { bestPrice = price; bestBook = b; }
                }
            }
            return bestBook;
        }
        let best = null, bestPrice = -Infinity;
        for (const [b, d] of Object.entries(bo)) {
            if (isUnbackableBookData(d, b)) continue;
            const price = d?.price;
            if (typeof price === 'number' && price > bestPrice) { bestPrice = price; best = b; }
        }
        return best;
    };
}
